import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { TableBody, TableCell, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface Props<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  label: string;
  onRowClick?: (item: TData) => void;
}

export function DataTableExpandableRows<TData, TValue>({
  columns,
  data,
  label,
  onRowClick,
}: Props<TData, TValue>) {
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Accordion key={label} asChild type="multiple">
      <AccordionItem asChild value={label}>
        <TableBody>
          <TableRow>
            <TableCell colSpan={columns.length}>
              <AccordionTrigger className="justify-start py-2 hover:no-underline">
                {label}
                <Badge variant="secondary">{data.length}</Badge>
              </AccordionTrigger>
            </TableCell>
          </TableRow>
          {table.getRowModel().rows?.length > 0 &&
            table.getRowModel().rows?.map((row) => (
              <AccordionContent key={row.id} asChild>
                <TableRow
                  className={cn(
                    'even:bg-gray-50',
                    onRowClick && 'cursor-pointer',
                  )}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={
                    onRowClick ? () => onRowClick(row.original) : undefined
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} width={cell.column.getSize()}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </AccordionContent>
            ))}
        </TableBody>
      </AccordionItem>
    </Accordion>
  );
}
